import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "mudcarp_cookie_consent_v1";
const CLARITY_PROJECT_ID = "wr43ph4sew";

type Consent = {
  analytics: boolean;
  marketing: boolean;
  decidedAt: string;
};

declare global {
  interface Window {
    dataLayer: unknown[];
    clarity?: (...args: unknown[]) => void;
  }
}

function gtag(...args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

function pushConsent(c: { analytics: boolean; marketing: boolean }) {
  gtag("consent", "update", {
    ad_storage: c.marketing ? "granted" : "denied",
    ad_user_data: c.marketing ? "granted" : "denied",
    ad_personalization: c.marketing ? "granted" : "denied",
    analytics_storage: c.analytics ? "granted" : "denied",
  });
}

function loadClarity() {
  if (window.clarity) return;
  (function (c: Window, l: Document, a: string, r: string, i: string) {
    (c as unknown as Record<string, unknown>)[a] =
      (c as unknown as Record<string, unknown>)[a] ||
      function (...args: unknown[]) {
        ((c as unknown as Record<string, unknown>)[a] as { q?: unknown[] }).q =
          ((c as unknown as Record<string, unknown>)[a] as { q?: unknown[] }).q || [];
        ((c as unknown as Record<string, unknown>)[a] as { q: unknown[] }).q.push(args);
      };
    const t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = "https://www.clarity.ms/tag/" + i;
    const y = l.getElementsByTagName(r)[0];
    y.parentNode?.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_PROJECT_ID);
}

function readStored(): Consent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Consent) : null;
  } catch {
    return null;
  }
}

function save(c: Consent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  } catch {
    /* private mode etc. — banner just keeps showing, no harm */
  }
}

export function CookieBanner() {
  const [show, setShow] = useState(false);
  const [details, setDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const stored = readStored();
    if (!stored) {
      setShow(true);
      return;
    }
    pushConsent(stored);
    if (stored.analytics) loadClarity();
  }, []);

  function commit(c: { analytics: boolean; marketing: boolean }) {
    const record: Consent = { ...c, decidedAt: new Date().toISOString() };
    save(record);
    pushConsent(c);
    if (c.analytics) loadClarity();
    setShow(false);
  }

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-label="Zgoda na pliki cookie"
      className="fixed inset-x-0 bottom-0 z-50 border-t-[3px] border-carp-ink bg-carp-paper shadow-brut-lg"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-3 p-4 md:p-6">
        <h2 className="font-display text-lg font-bold uppercase tracking-tight md:text-xl">
          Ciasteczka 🍪 + analityka
        </h2>
        <p className="text-sm leading-snug text-carp-ink/80">
          Używamy plików cookie. Niezbędne (logowanie, sesja, koszyk) działają zawsze.
          Analityka (Google Analytics, Microsoft Clarity) i marketing wymagają twojej zgody.
          Szczegóły w{" "}
          <Link to="/polityka-prywatnosci" className="link-brut">
            polityce prywatności
          </Link>
          .
        </p>

        {details && (
          <div className="space-y-2 border-[3px] border-carp-ink bg-white p-3">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked
                disabled
                className="mt-1 h-5 w-5 accent-carp-ink"
              />
              <span className="text-sm">
                <strong className="font-display">Niezbędne</strong> — sesja, logowanie z Kick, płatność.
                Bez tego strona nie działa. Zawsze włączone.
              </span>
            </label>
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="mt-1 h-5 w-5 accent-carp-primary"
              />
              <span className="text-sm">
                <strong className="font-display">Analityka</strong> — Google Analytics 4 i Microsoft Clarity.
                Mierzymy ruch i nagrywamy sesje, żeby naprawiać UX. Bez tego nie wiemy co działa.
              </span>
            </label>
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="mt-1 h-5 w-5 accent-carp-primary"
              />
              <span className="text-sm">
                <strong className="font-display">Marketing</strong> — remarketing i reklamy.
                Dziś nieaktywne, ale rezerwujemy na przyszłość.
              </span>
            </label>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-primary"
            onClick={() => commit({ analytics: true, marketing: true })}
          >
            Akceptuj wszystkie
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => commit({ analytics: false, marketing: false })}
          >
            Tylko niezbędne
          </button>
          {details ? (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => commit({ analytics, marketing })}
            >
              Zapisz wybór
            </button>
          ) : (
            <button
              type="button"
              className="ml-auto text-sm font-bold underline decoration-carp-yellow decoration-[3px] underline-offset-2 hover:bg-carp-yellow hover:text-carp-ink"
              onClick={() => setDetails(true)}
            >
              Dostosuj
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
